import {
  axiosGet,
  axiosPost,
  axiosPatch,
  axiosDelete,
  axiosMultipart,
} from 'utils/axios';
import { BASE_URL } from 'utils/constants';
import {
  CSRF_URL,
  AUTH_URL,
  STORE_URL,
  PRODUCT_URL,
  ORDER_URL,
  CART_URL,
  STORE_SHOPPER_URL,
  VERIFY_URL,
  DELIVERY_ADDRESS_URL,
  SEARCH_URL,
  USER_URL,
  COMPANY_URL,
  CLIENT_URL,
  MERCHANT_CATEGORY_CODE_URL,
  TRANSACTION_URL,
  REFUND_URL,
  PAYOUT_URL,
  WEBHOOK_URL,
  TRANSFER_URL,
  DISPUTE_URL,
  ACTION_URL,
  FEE_STRUCTURE_URL,
} from './urls';

const GET_AUTH = () => {
  return axiosGet({
    baseURL: BASE_URL,
    url: CSRF_URL,
  });
};

export const POST_LOGIN = (payload) => {
  const { email, password } = payload;
  return GET_AUTH().then(() => {
    return axiosPost({
      data: {
        email,
        password,
        remember: true,
      },
      url: `${AUTH_URL}/login`,
    }).then(({ data }) => data);
  });
};

export const POST_LOGOUT = () => {
  return axiosPost({
    url: `${AUTH_URL}/logout`,
  });
};

export const POST_FORGOT_PASSWORD = ({ email, return_url }) => {
  return GET_AUTH().then(() => {
    return axiosPost({
      url: `${AUTH_URL}/forgot_password`,
      data: {
        email,
        return_url,
      },
    });
  });
};

export const GET_ME = () => {
  const params = { include: 'permissions' };
  return GET_AUTH().then(() => {
    return axiosPost({
      url: `${USER_URL}/me`,
      params,
    }).then(({ data }) => data);
  });
};

export const POST_RESET_PASSWORD = ({
  token,
  email,
  password,
  password_confirmation,
}) => {
  return GET_AUTH().then(() => {
    return axiosPost({
      url: `${AUTH_URL}/reset_password`,
      data: {
        token,
        email,
        password,
        password_confirmation,
      },
    });
  });
};

export const GET_STORE_LIST = () => {
  return axiosGet({
    url: STORE_URL,
    params: { include: 'client' },
  }).then(({ data }) => data);
};

export const GET_STORE_BY_ID = (id) => {
  return axiosGet({
    url: `${STORE_URL}/${id}`,
    params: { include: 'client,company,company.integration' },
  }).then(({ data }) => data);
};

export const GET_STORE_INSIGHTS = (id) => {
  const params = { product_type: 'fundraising_campaign' };
  return axiosGet({
    url: `${STORE_URL}/${id}/insights`,
    params,
  }).then(({ data }) => data);
};

export const GET_TERMINAL_SHIPPING_METHODS = (id, country = null) => {
  const params = { status: 'available', country };
  return axiosGet({
    url: `${STORE_URL}/${id}/terminal_shipping_methods`,
    params,
  }).then(({ data }) => data);
}

export const POST_STORE_SELECT = (store_id) => {
  return axiosPost({
    url: `${STORE_URL}/select`,
    data: { store_id },
  }).then(({ data }) => data);
};

export const GET_PRODUCT_LIST = (_params) => {
  const params = {
    order_by: 'created_at',
    order_direction: 'desc',
    ..._params,
  };
  return axiosGet({
    url: PRODUCT_URL,
    params,
  }).then(({ data }) => data);
};
export const GET_PRODUCT_BY_ID = (id) => {
  return axiosGet({
    url: `${PRODUCT_URL}/${id}`,
  }).then(({ data }) => data);
};
export const POST_PRODUCT = (payload) => {
  return axiosPost({
    url: PRODUCT_URL,
    data: payload,
  }).then(({ data }) => data);
};
export const PATCH_PRODUCT = (payload) => {
  const { id, ..._payload } = payload;
  return axiosPatch({
    url: `${PRODUCT_URL}/${id}`,
    data: _payload,
  }).then(({ data }) => data);
};
export const POST_PRODUCT_SHARE = (payload) => {
  const { id, ..._payload } = payload;
  return axiosPost({
    url: `${PRODUCT_URL}/${id}/share`,
    data: _payload,
  }).then(({ data }) => data);
};

export const GET_ORDER_LIST = (_params) => {
  let params = {
    include: 'orderProducts.product',
    order_by: 'created_at',
    order_direction: 'desc',
    product_type: 'fundraising_campaign',
    ..._params,
  };

  return axiosGet({
    url: ORDER_URL,
    params,
  }).then(({ data }) => data);
};
export const GET_ORDER = (id, token) => {
  let params = { token, include: 'orderProducts.product,store.client,store.company.integration' };

  return axiosGet({
    url: `${ORDER_URL}/${id}`,
    params,
  }).then(({ data }) => data);
};
export const GET_STRIPE_ORDER = (id, token) => {
  let params = { token };

  return axiosGet({
    url: `${ORDER_URL}/${id}/stripe`,
    params
  }).then(({ data }) => data);
};
export const POST_ACCEPT_ORDER = (id) => {
  return axiosPost({
    url: `${ORDER_URL}/${id}/accept`,
  }).then(({ data }) => data);
};
export const POST_REJECT_ORDER = (id) => {
  return axiosPost({
    url: `${ORDER_URL}/${id}/reject`,
  }).then(({ data }) => data);
};

export const POST_CANCEL_ORDER = (id, params) => {
  return axiosPost({
    url: `${ORDER_URL}/${id}/cancel`,
    params
  }).then(({ data }) => data);
}

export const POST_COMPLETE_ORDER = (id) => {
  return axiosPost({
    url: `${ORDER_URL}/${id}/complete`,
  }).then(({ data }) => data);
};
export const POST_SHIPPED_ORDER = (id, tracking_id) => {
  return axiosPost({
    url: `${ORDER_URL}/${id}/shipped`,
    data: {
      tracking_id,
    },
  }).then(({ data }) => data);
};
export const POST_SEND_RECEIPT = (id) => {
  return axiosPost({
    url: `${ORDER_URL}/${id}/send_receipt`,
  }).then(({ data }) => data);
};
export const POST_ORDER = (payload) => {
  return axiosPost({
    url: decodeURIComponent(
      `${ORDER_URL}?checkout_url=${window.location.origin}`
    ),
    data: payload,
  }).then(({ data }) => data);
};
export const POST_EXPORT_ORDERS = ({ from, status }) => {
  let params = {
    order_by: 'created_at',
    order_direction: 'desc',
    from,
  };
  if (status) params.status = status;

  return axiosPost({
    url: `${ORDER_URL}/export`,
    params,
  }).then(({ data, status: _status }) => ({ data, status: _status }));
};

export const GET_CART = (storeId, user_pays_fees) => {
  let params = {};
  if (typeof user_pays_fees === 'boolean')
    params.user_pays_fees = user_pays_fees;
  return axiosGet({
    url: `${STORE_URL}/${storeId}${CART_URL}`,
    params,
  }).then(({ data }) => data);
};
export const POST_CART = (payload) => {
  return axiosPost({
    url: CART_URL,
    data: payload,
  }).then(({ data }) => data);
};
export const PATCH_CART_PRODUCT = (cartProductId, payload, user_pays_fees) => {
  let params = {};
  if (typeof user_pays_fees === 'boolean')
    params.user_pays_fees = user_pays_fees;
  return axiosPatch({
    url: `${CART_URL}/${cartProductId}`,
    params,
    data: payload,
  }).then(({ data }) => data);
};
export const DELETE_CART_PRODUCT = (productId) => {
  return axiosDelete({
    url: `${CART_URL}/${productId}`,
  }).then(({ data }) => data);
};
export const CLEAN_CART = (storeId) => {
  return axiosPost({
    url: `${CART_URL}/${storeId}/clean`,
  }).then(({ data }) => data);
};
export const POST_CALCULATE_FEES = (companyId, payload) => {
  return axiosPost({
    url: `${COMPANY_URL}/${companyId}/calculate_fees`,
    data: payload,
  }).then(({ data }) => data);
};

export const POST_STORE_SHOPPER = (payload) => {
  return axiosPost({
    url: STORE_SHOPPER_URL,
    data: payload,
  }).then(({ data }) => data);
};
export const GET_STORE_SHOPPER = (payload) => {
  return axiosPost({
    url: `${STORE_SHOPPER_URL}/me`,
  }).then(({ data }) => data);
};
export const POST_SHOPPER_LOGOUT = (payload, verificationCodeId) => {
  return axiosPost({
    url: `${STORE_SHOPPER_URL}/logout`,
  }).then(({ data }) => data);
};

export const POST_VERIFY_CHECKOUT_STORE = (payload) => {
  return axiosPost({
    url: `${VERIFY_URL}/checkout_store`,
    data: payload,
  }).then(({ data }) => data);
};
export const POST_VERIFY_BY_CODE = (payload, verificationCodeId) => {
  return axiosPost({
    url: `${VERIFY_URL}/${verificationCodeId}/verify`,
    data: payload,
  }).then(({ data }) => data);
};

export const POST_DELIVERY_ADDRESS = (payload) => {
  return axiosPost({
    url: DELIVERY_ADDRESS_URL,
    data: { ...payload, alias: `alias-${Math.random()}` },
  }).then(({ data }) => data);
};
export const GET_DELIVERY_ADDRESS = () => {
  return axiosPost({
    url: `${DELIVERY_ADDRESS_URL}/my`,
  }).then(({ data }) => data);
};

export const GET_CITY = ({ country, term }, cancelToken) => {
  return axiosGet({
    url: `${SEARCH_URL}/city`,
    params: { country, term },
    cancelToken,
  }).then(({ data }) => data);
};
export const GET_COUNTRY = ({ term }, cancelToken) => {
  return axiosGet({
    url: `${SEARCH_URL}/country`,
    params: { term },
    cancelToken,
  }).then(({ data }) => data);
};
export const PATCH_USER = (payload) => {
  return axiosPatch({
    url: `${USER_URL}`,
    data: payload,
  }).then(({ data }) => data);
};

export const POST_CHECKOUT_PAYMENT_METHODS = (storeId) => {
  return axiosPost({
    url: `${STORE_URL}/${storeId}/checkout_payment_methods`,
  }).then(({ data }) => data);
};

export const POST_CREATE_STRIPE_PAYMENT_INTENT = (
  store_id,
  products,
  address,
  comments,
  is_anonymous,
  billing_address,
  shipping_method,
  user_pays_fees
) => {
  const payload = {
    store_id,
    products,
    address,
    comments,
    is_anonymous,
    billing_address,
    shipping_method,
    user_pays_fees,
  };

  return axiosPost({
    url: '/order/new_payment_intent',
    data: payload,
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    validateStatus: false
  }).then(({ data }) => data);
}

export const POST_VERIFY_STRIPE_PAYMENT_INTENT = (
  storeId,
  paymentIntentId
) => {
  return axiosPost({
    url: `/store/${storeId}/${paymentIntentId}/verify_payment_intent`
  }).then(({ data }) => data);
};

export const POST_PREVIEW_STRIPE_TERMINAL_ORDER = (
  store_id,
  products,
  address,
  comments,
  is_anonymous,
  billing_address,
  shipping_method,
  user_pays_fees
) => {
  const payload = {
    store_id,
    products,
    address,
    comments,
    is_anonymous,
    billing_address,
    shipping_method,
    user_pays_fees,
  };

  return axiosPost({
    url: '/order/preview',
    data: payload,
  }).then(({ data }) => data);
};

export const POST_APPLE_PAY_SESSION = (payload) => {
  return axiosPost({
    url: '/apple_pay_session',
    data: payload,
  });
};
export const GET_COMPANY_LIST = (term, cancelToken) => {
  const params = term ? { term } : {};
  return axiosGet({
    url: `${COMPANY_URL}`,
    params,
    cancelToken,
  }).then(({ data }) => data);
};
export const GET_COMPANIES = (params) => {
  return axiosGet({
    url: `${COMPANY_URL}`,
    params: {
      ...params,
      include: 'feeStructure',
    },
  }).then(({ data }) => data);
};
export const GET_COMPANY_BY_ID = (id) => {
  return axiosGet({
    url: `${COMPANY_URL}/${id}`,
  }).then(({ data }) => data);
};
export const GET_CLIENT_BY_ID = async (id, params) => {
  return axiosGet({
    url: `${CLIENT_URL}/${id}`,
    params: {
      ...params,
      include:
        'admins,companies,defaultFeeStructure,defaultRole,integration,liableCompany',
    },
  }).then(({ data }) => data);
};
export const GET_CLIENTS = (params) => {
  return axiosGet({
    url: `${CLIENT_URL}`,
    params: params,
  }).then(({ data }) => data);
};

export const GET_USER_CLIENTS = (params) => {
  return axiosGet({
    url: `${CLIENT_URL}/my`,
    params: params,
  }).then(({ data }) => data);
};

export const DELETE_CLIENT = async (clientId) => {
  return axiosDelete({
    url: `${CLIENT_URL}/${clientId}`,
  }).then(({ data }) => data);
};
export const POST_COMPANY = (companyPayload, storePayload) => {
  return axiosPost({
    url: COMPANY_URL,
    data: companyPayload,
  }).then(({ data }) => {
    if (data.data.id) {
      return POST_STORE({
        ...storePayload,
        company_id: data.data.id,
        display_price_tax_excl: true,
        auto_accept_orders: true,
        tax_rate: 0,
      });
    }
  });
};
export const POST_COMPANY_CREATE = (companyPayload) => {
  return axiosPost({
    url: COMPANY_URL,
    data: companyPayload,
  }).then(({ data }) => data);
};
export const POST_COMPANY_SELECT = (company_id) => {
  return axiosPost({
    url: `${COMPANY_URL}/select`,
    data: { company_id },
  }).then(({ data }) => data);
};

export const POST_STORE = (payload) => {
  return axiosPost({
    url: STORE_URL,
    data: payload,
  }).then(({ data }) => data);
};

export const POST_ACCOUNT_HOLDER = (id, payload) => {
  return axiosPost({
    url: `${COMPANY_URL}/${id}/account_holder`,
    data: payload,
  }).then(({ data }) => data);
};
export const GET_MERCHANT_CATEGORY_CODE = (search) => {
  const params = search ? { search } : {};
  return axiosGet({
    url: MERCHANT_CATEGORY_CODE_URL,
    params,
  }).then(({ data }) => data);
};

export const POST_HOP_URL = (id) => {
  return axiosPost({
    url: `${COMPANY_URL}/${id}/generate_hop_url `,
    data: {
      return_url: window.location.origin,
    },
  }).then(({ data }) => data);
};
export const POST_UPDATE_KYC_STATUS = (id) => {
  return axiosPost({
    url: `${COMPANY_URL}/${id}/account_holder/disable_kyc_just_approved`,
  }).then(({ data }) => data);
};

export const POST_VERIFY_EMAIL = (code) => {
  return axiosPost({
    url: `${USER_URL}/verify_email`,
    data: { code },
  }).then(({ data }) => data);
};

export const POST_RESEND_VERIFICATION = () => {
  return axiosPost({
    url: `${USER_URL}/resend_verification`,
  }).then(({ data }) => data);
};

export const GET_TRANSACTIONS = (params) => {
  return axiosGet({
    url: `${TRANSACTION_URL}`,
    params,
  }).then(({ data }) => data);
};
export const GET_TRANSACTIONS_BY_ID = (id) => {
  return axiosGet({
    url: `${TRANSACTION_URL}/${id}`,
    params: {
      include:
        'chargebackFees,company,events,link,actions,feeStructure,modelEvents',
    },
  }).then(({ data }) => data);
};
export const GET_REFUNDS = (params) => {
  return axiosGet({
    url: `${REFUND_URL}`,
    params,
  }).then(({ data }) => data);
};
export const GET_PAYOUTS = (params) => {
  return axiosGet({
    url: `${PAYOUT_URL}`,
    params,
  }).then(({ data }) => data);
};
export const GET_PAYOUTS_BY_ID = (id) => {
  return axiosGet({
    url: `${PAYOUT_URL}/${id}`,
    params: {
      include: 'company,webhooks',
    },
  }).then(({ data }) => data);
};
export const GET_WEBHOOKS = (params) => {
  return axiosGet({
    url: `${WEBHOOK_URL}`,
    params,
  }).then(({ data }) => data);
};
export const GET_WEBHOOKS_BY_ID = (id) => {
  return axiosGet({
    url: `${WEBHOOK_URL}/${id}`,
  }).then(({ data }) => data);
};
export const GET_TRANSFERS = (params) => {
  return axiosGet({
    url: `${TRANSFER_URL}`,
    params,
  }).then(({ data }) => data);
};
export const GET_TRANSFERS_BY_ID = (id) => {
  return axiosGet({
    url: `${TRANSFER_URL}/${id}`,
  }).then(({ data }) => data);
};
export const GET_DISPUTES = (params) => {
  return axiosGet({
    url: `${DISPUTE_URL}`,
    params,
  }).then(({ data }) => data);
};
export const GET_DISPUTES_BY_ID = (id) => {
  return axiosGet({
    url: `${DISPUTE_URL}/${id}`,
    params: {
      include: 'transaction,transaction.integration,transaction.company',
    },
  }).then(({ data }) => data);
};
export const GET_DISPUTES_ADYEN_REASONS = (id) => {
  return axiosGet({
    url: `${DISPUTE_URL}/${id}/adyen/reasons`,
  }).then(({ data }) => data);
};
export const GET_STRIPE_DISPUTE = (id) => {
  return axiosGet({
    url: `${DISPUTE_URL}/${id}/stripe/dispute`,
  }).then(({ data }) => data);
};
export const SUBMIT_STRIPE_DISPUTE = (id, data) => {
  return axiosPost({
    url: `${DISPUTE_URL}/${id}/stripe/save`,
    data,
  });
};
export const POST_EXPORT_DISPUTES = (params) => {
  return axiosPost({
    url: `${DISPUTE_URL}/export`,
    params,
  }).then(({ data }) => data);
};
export const GET_ACTIONS = (params) => {
  return axiosGet({
    url: `${ACTION_URL}`,
    params,
  }).then(({ data }) => data);
};
export const POST_TRANSACTION_REFUND = ({ id, amount, reason }) => {
  return axiosPost({
    url: `${TRANSACTION_URL}/${id}/refund`,
    data: {
      amount,
      reason,
    },
  }).then(({ data }) => data);
};
export const POST_TRANSACTION_CAPTURE = ({ id, amount }) => {
  return axiosPost({
    url: `${TRANSACTION_URL}/${id}/capture`,
    data: {
      amount,
    },
  }).then(({ data }) => data);
};
export const POST_TRANSACTION_ADJUST = ({ id, amount }) => {
  return axiosPost({
    url: `${TRANSACTION_URL}/${id}/adjust_auth`,
    data: {
      amount,
    },
  }).then(({ data }) => data);
};
export const GET_FEE_STRUCTURE = (params) => {
  return axiosGet({
    url: `${FEE_STRUCTURE_URL}`,
    params,
  }).then(({ data }) => data);
};
export const FAKE_POST_SEND_RECEIPT = () => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve({ success: true });
    }, 2500);
  });
};
export const POST_EXPORT_TRANSACTIONS = ({
  status,
  from,
  to,
  fraud_score_max,
  fraud_score_min,
  payout_id,
  ...amountParam
}) => {
  let params = {};

  if (!payout_id) {
    params = {
      from,
      to,
      ...amountParam,
    };
    if (status) params.status = status;
    if (fraud_score_max) params.fraud_score_max = fraud_score_max;
    if (fraud_score_min) params.fraud_score_min = fraud_score_min;
  } else {
    // only use payout_id param on payout transactions export
    params = { payout_id };
  }

  return axiosPost({
    url: `${TRANSACTION_URL}/export`,
    params,
  });
};

export const POST_EXPORT_PAYOUTS = ({ status, from, to, term }) => {
  let params = {
    from,
  };
  if (status) params.status = status;
  if (to) params.to = to;
  if (term) params.term = term;

  return axiosPost({
    url: `${PAYOUT_URL}/export`,
    params,
  });
};

export const POST_EXPORT_REFUNDS = (params) => {
  return axiosPost({
    url: `${REFUND_URL}/export`,
    params,
  }).then(({ data }) => data);
};

export const UPLOAD_ADYEN_DISPUTE_DEFENCE = async (disputeId, formData) => {
  return axiosMultipart({
    url: `${DISPUTE_URL}/${disputeId}/adyen/upload`,
    data: formData,
  }).then(({ data }) => data);
};

export const DELETE_ADYEN_DISPUTE_DEFENCE = async (disputeId, fileTypeCode) => {
  return axiosGet({
    url: `${DISPUTE_URL}/${disputeId}/adyen/remove/${fileTypeCode}`,
  }).then(({ data }) => data);
};

export const SUBMIT_ADYEN_DISPUTE_EVIDENCE = (disputeId, defenceReasonCode) => {
  return axiosGet({
    url: `${DISPUTE_URL}/${disputeId}/adyen/submit/${defenceReasonCode}`,
  }).then(({ data }) => data);
};

export const GET_FILE_INFO = (disputeId, documentId) => {
  return axiosGet({
    url: `${DISPUTE_URL}/${disputeId}/stripe/file/${documentId}`,
  }).then(({ data }) => data);
};
